import type { TFunction } from 'i18next';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { PasswordInput } from '../../components/PasswordInput';
import { useFeatures } from '../../hooks/useFeatures';
import { useIdpSession } from '../../hooks/useIdpSession';
import { APIError, idpApi, type ProfileResponse } from '../../services/idp-api';

function getCredentialErrorMessage(error: unknown, t: TFunction<'idp'>): string {
  if (!(error instanceof APIError)) {
    return t('credentials.errors.default');
  }

  if (error.code === 'LAST_CREDENTIAL') {
    return t('credentials.errors.lastCredential');
  }

  if (error.code === 'INVALID_CREDENTIALS') {
    return t('credentials.errors.invalidCredentials');
  }

  if (error.code === 'PASSWORD_COMPROMISED') {
    return t('credentials.errors.passwordCompromised');
  }

  return error.message;
}

export function CredentialsPage() {
  const { t } = useTranslation('idp');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { google: googleEnabled } = useFeatures();
  const { isAuthenticated, isLoading } = useIdpSession();
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [pageLoading, setPageLoading] = useState(true);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);
  const [isUnlinkingGoogle, setIsUnlinkingGoogle] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const hasGoogleCredential = useMemo(
    () => profile?.credentials.some((credential) => credential.type === 'google') ?? false,
    [profile]
  );

  async function loadProfile() {
    const nextProfile = await idpApi.getProfile();
    setProfile(nextProfile);
  }

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth/signin', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void (async () => {
      try {
        await loadProfile();
      } finally {
        setPageLoading(false);
      }
    })();
  }, [isAuthenticated]);

  useEffect(() => {
    const code = searchParams.get('code')?.trim() ?? '';
    const identityId = searchParams.get('identityId')?.trim() ?? '';
    const googleError = searchParams.get('error')?.trim() ?? '';

    if (code === '' && googleError === '') {
      return;
    }

    if (googleError !== '') {
      setErrorMessage(t('credentials.googleLinkCancelledError'));
      return;
    }

    if (code === '' || identityId === '') {
      setErrorMessage(t('credentials.googleLinkExpiredError'));
      return;
    }

    setIsLinkingGoogle(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    void (async () => {
      try {
        await idpApi.linkGoogleCallback({ code, identityId });
        await loadProfile();
        setSuccessMessage(t('credentials.googleLinkSuccess'));
      } catch (error) {
        setErrorMessage(getCredentialErrorMessage(error, t));
      } finally {
        setIsLinkingGoogle(false);
      }
    })();
  }, [searchParams, t]);

  async function handleChangePassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsChangingPassword(true);

    try {
      await idpApi.changePassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setSuccessMessage(t('credentials.passwordChangeSuccess'));
    } catch (error) {
      setErrorMessage(getCredentialErrorMessage(error, t));
    } finally {
      setIsChangingPassword(false);
    }
  }

  async function handleLinkGoogle() {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsLinkingGoogle(true);

    try {
      const response = await idpApi.linkGoogle();
      window.location.assign(response.redirectUri);
    } catch (error) {
      setErrorMessage(getCredentialErrorMessage(error, t));
      setIsLinkingGoogle(false);
    }
  }

  async function handleUnlinkGoogle() {
    setErrorMessage(null);
    setSuccessMessage(null);
    setIsUnlinkingGoogle(true);

    try {
      await idpApi.unlinkGoogle();
      await loadProfile();
      setSuccessMessage(t('credentials.googleUnlinkSuccess'));
    } catch (error) {
      setErrorMessage(getCredentialErrorMessage(error, t));
    } finally {
      setIsUnlinkingGoogle(false);
    }
  }

  if (isLoading || pageLoading) {
    return (
      <Stack alignItems="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h5" component="h1" fontWeight={700}>
        {t('credentials.title')}
      </Typography>

      {successMessage !== null ? <Alert severity="success">{successMessage}</Alert> : null}
      {errorMessage !== null ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Card>
        <CardContent>
          <Stack
            spacing={2}
            component="form"
            onSubmit={(event) => {
              void handleChangePassword(event);
            }}
          >
            <Typography variant="h6" component="h2" fontWeight={600}>
              {t('credentials.changePasswordTitle')}
            </Typography>
            <PasswordInput
              label={t('credentials.currentPasswordLabel')}
              autoComplete="current-password"
              value={currentPassword}
              onChange={(event) => {
                setCurrentPassword(event.target.value);
              }}
              showStrength={false}
              fullWidth
              required
            />
            <PasswordInput
              label={t('credentials.newPasswordLabel')}
              autoComplete="new-password"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
              }}
              fullWidth
              required
            />
            <Button
              type="submit"
              variant="contained"
              disabled={isChangingPassword || currentPassword === '' || newPassword.length < 12}
            >
              {isChangingPassword ? t('credentials.updatingButton') : t('credentials.updateButton')}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {googleEnabled ? (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6" component="h2" fontWeight={600}>
                {t('credentials.googleTitle')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {hasGoogleCredential
                  ? t('credentials.googleLinked')
                  : t('credentials.googleNotLinked')}
              </Typography>
              {hasGoogleCredential ? (
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    void handleUnlinkGoogle();
                  }}
                  disabled={isUnlinkingGoogle}
                >
                  {isUnlinkingGoogle
                    ? t('credentials.unlinkingButton')
                    : t('credentials.unlinkButton')}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => {
                    void handleLinkGoogle();
                  }}
                  disabled={isLinkingGoogle}
                >
                  {isLinkingGoogle ? t('credentials.linkingButton') : t('credentials.linkButton')}
                </Button>
              )}
            </Stack>
          </CardContent>
        </Card>
      ) : null}

      <Link component={RouterLink} to="/account" underline="hover">
        {t('credentials.backToDashboard')}
      </Link>
    </Stack>
  );
}
